/* eslint-disable react/function-component-definition */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { getCookies } from "../../hooks/useCookies";
import { REACT_APP_API_VENDOR_EXPORT } from "../../redux/apiConstants";
import styles from "../users/UserExportModal.module.scss";

const EXPORT_FORMATS = [
  { key: "csv", label: "CSV", icon: "fa-file-csv", description: "Comma-separated values" },
  { key: "xlsx", label: "Excel", icon: "fa-file-excel", description: "Microsoft Excel format" },
];

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const VendorExportModal = ({ show, handleClose, searchBy }) => {
  const [selectedFormat, setSelectedFormat] = useState("csv");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const pollTimerRef = useRef(null);
  const token = getCookies("Token");

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const resetState = () => {
    clearPollTimer();
    setSelectedFormat("csv");
    setLoading(false);
    setStatusMessage("");
  };

  useEffect(() => () => clearPollTimer(), []);

  const handleCloseModal = () => {
    if (loading) return;
    resetState();
    handleClose();
  };

  const downloadExportFile = async (exportId, filename) => {
    const response = await axios.get(
      `${REACT_APP_API_VENDOR_EXPORT}/${exportId}/download`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || `vendors-export.${selectedFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const pollExportStatus = async (exportId) => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
      const { data } = await axios.get(
        `${REACT_APP_API_VENDOR_EXPORT}/${exportId}/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const job = data?.data?.result;
      if (!job) {
        throw new Error("Export status unavailable");
      }

      if (job.status === "completed") {
        setStatusMessage(`Preparing download for ${job.count ?? 0} vendors...`);
        await downloadExportFile(exportId, job.filename);
        toast.success("Vendors exported successfully!");
        setLoading(false);
        resetState();
        handleClose();
        return;
      }

      if (job.status === "failed") {
        throw new Error(job.error || "Export failed");
      }
      setStatusMessage("Please wait we are preparing your export file..");
      await sleep(POLL_INTERVAL_MS);
    }

    throw new Error("Export timed out. Please try again.");
  };

  const handleExport = async () => {
    setLoading(true);
    setStatusMessage("Starting export...");

    try {
      const payload = { format: selectedFormat };
      if (searchBy) {
        payload.searchBy = searchBy;
      }

      const { data } = await axios.post(REACT_APP_API_VENDOR_EXPORT, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const exportId = data?.data?.result?.exportId;
      if (!exportId) {
        throw new Error("Failed to start export");
      }

      setStatusMessage("Export started. You can keep using the admin while we prepare your file.");
      await pollExportStatus(exportId);
    } catch (error) {
      console.error("Export error:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Export failed. Please try again.";
      toast.error(message);
      setLoading(false);
      setStatusMessage("");
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      centered
      className={styles.exportModal}
    >
      <div className={styles.modalHeader}>
        <h5 className={styles.modalTitle}>
          <i className="fa fa-download" />
          Export Vendors
        </h5>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={handleCloseModal}
          disabled={loading}
          aria-label="Close modal"
        >
          <i className="fa fa-times" />
        </button>
      </div>

      <Modal.Body className={styles.modalBody}>
        {loading ? (
          <div className={styles.loadingBox}>
            <Spinner animation="border" className={styles.spinner} />
            <h5>Exporting Vendors...</h5>
            <p>{statusMessage || "Please wait while we prepare your export file."}</p>
          </div>
        ) : (
          <div className={styles.formatSection}>
            <h6 className={styles.sectionTitle}>
              <i className="fa fa-file-export" />
              Select Export Format
            </h6>
            {searchBy && (
              <p className={styles.searchHint}>
                Export will include vendors matching your current search.
              </p>
            )}
            <div className={styles.formatOptions}>
              {EXPORT_FORMATS.map((format) => (
                <button
                  key={format.key}
                  type="button"
                  className={`${styles.formatBtn} ${
                    selectedFormat === format.key ? styles.active : ""
                  }`}
                  onClick={() => setSelectedFormat(format.key)}
                  aria-label={`Export as ${format.label}`}
                >
                  <i className={`fa ${format.icon}`} />
                  <span className={styles.formatLabel}>{format.label}</span>
                  <span className={styles.formatDesc}>{format.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>

      <div className={styles.modalFooter}>
        <Button
          variant="secondary"
          onClick={handleCloseModal}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          className={styles.primaryBtn}
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" />
              Exporting...
            </>
          ) : (
            <>
              <i className="fa fa-download" />
              Export {selectedFormat.toUpperCase()}
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
};

VendorExportModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  searchBy: PropTypes.string,
};

VendorExportModal.defaultProps = {
  searchBy: null,
};

export default VendorExportModal;
